import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getChats } from "../../data/Chat/getChats";
import { updateSeen } from "../../data/Chat/updateSeen";
import { createWebSocket, sendWebSocketMessage } from "./utils";
import { useGlobalContext } from "../global";
import { getContacts } from "../../data/Chat/getContacts";
import { createChat as createChatService } from "../../data/Chat/createChat";
import { getToken, isClient } from "../../utils/account";

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { currentUser, accessToken } = useGlobalContext();

  const socket = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [chats, setChats] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedChat, setSelectedChat] = useState();
  const [receivedMessage, setReceivedMessage] = useState();

  useEffect(() => {
    if (receivedMessage) {
      handleReceivedMessage(receivedMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedMessage]);

  useEffect(() => {
    if (selectedChat && selectedChat.unSeenCount > 0) {
      updateSeen(selectedChat.id);
      selectedChat.unSeenCount = 0;
      setChats([...chats]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  const addNewMessageToChat = (message) => {
    const update = chats => {
      const chat = chats.find(chat => chat.id === message.chatId);
      if (!chat.messages.some(m => m.id === message.id)) {
        chat.messages.push(message);
      }
      if (selectedChat?.id !== chat.id) {
        chat.unSeenCount++;
      } else {
        updateSeen(chat.id);
      }
      setChats([...chats]);
      if (selectedChat) {
        setSelectedChat({ ...selectedChat });
      }
    };

    if (chats.some(chat => chat.id === message.chatId)) {
      update(chats);
    } else {
      loadData().then(({ chats }) => {
        update(chats);
      });
    }
  }

  const updateChatTextMessage = (data) => {
    const { chatId, messageId, text } = data;
    const chat = chats.find(chat => chat.id === chatId);
    const message = chat.messages.find(m => m.id === messageId);
    message.message.content = text;
    setChats([...chats]);
  }

  const deleteChatMessage = (data) => {
    const { chatId, messageId } = data;
    const chat = chats.find(chat => chat.id === chatId);
    chat.messages = chat.messages.filter(m => m.id !== messageId);
    setChats([...chats]);
  }

  const handleReceivedMessage = (message) => {
    const actionHandlers = {
      "newMessage": addNewMessageToChat,
      "updateTextMessage": updateChatTextMessage,
      "deleteMessage": deleteChatMessage
    }

    const actionHandler = actionHandlers[message.action];
    actionHandler(message.data);
  }

  const initialize = token => {
    if (!socket.current && token) {
      socket.current = createWebSocket({
        token,
        onOpen: () => {
          setIsConnected(true);
        },
        onMessage: setReceivedMessage,
        onError: () => {
          setIsConnected(false);
        },
        onClose: async () => {
          setIsConnected(false);
          socket.current = null;
          const token = getToken();
          if (token) {
            console.log("Socket closed... reconnecting in 3 seconds");
            setTimeout(() => {
              initialize(token);
            }, 3000);
          } else {
            console.log("user logged out and socket closed");
          }
        }
      });
    }
  };

  const loadData = async () => {
    if (!isLoading) {
      setIsLoading(true);
    }
    const [chats, contacts] = await Promise.all([getChats(), getContacts()]);
    setContacts(contacts);
    setChats(chats);
    setIsLoading(false);
    return { chats, contacts };
  };

  const sendTextMessage = message => {
    const data = {
      token: accessToken,
      chatId: selectedChat.id,
      message: {
        type: "text",
        content: message
      }
    };
    sendWebSocketMessage({
      socket: socket.current,
      message: data,
      action: "sendMessage"
    });
  };

  const updateTextMessage = (message, newContent) => {
    const data = {
      token: accessToken,
      chatId: message.chatId,
      messageId: message.id,
      text: newContent
    };
    sendWebSocketMessage({
      socket: socket.current,
      message: data,
      action: "updateTextMessage"
    });
    const chat = chats.find(p => p.id === message.chatId);
    const existingMessage = chat.messages.find(p => p.id === message.id);
    existingMessage.message.content = newContent;
    setChats([...chats]);
  };

  const deleteMessage = (message) => {
    const data = {
      token: accessToken,
      chatId: message.chatId,
      messageId: message.id,
    };
    sendWebSocketMessage({
      socket: socket.current,
      message: data,
      action: "deleteMessage"
    });
    const chat = chats.find(p => p.id === message.chatId);
    chat.messages = chat.messages.filter(p => p.id !== message.id);
    setChats([...chats]);
  }

  const createChat = async contact => {
    contact.isLoading = true;
    setContacts([...contacts]);
    const { data } = await createChatService([contact.id]);
    const { chats } = await loadData();
    const chat = chats.find(chat => chat.id === data.chatId);
    setSelectedChat(chat);
    contact.isLoading = false;
    setContacts([...contacts]);
  };

  useEffect(() => {
    const dispose = () => {
      socket.current?.close();
      socket.current = null;
      setIsConnected(false);
      setChats([]);
    };

    if (currentUser === null) {
      dispose();
    } else if (!isClient()) {
      initialize(accessToken);
      loadData();
    }
    return () => {
      dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const value = {
    isConnected,
    chats,
    contacts,
    createChat,
    selectedChat,
    setSelectedChat,
    sendTextMessage,
    updateTextMessage,
    deleteMessage,
    loadData
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => useContext(ChatContext);
